export const TYPEAHEAD_DELAY = 300;
export const TYPEAHEAD_RELEASE_DELAY = 1000;

/**
 * We ignore these printable characters,
 * for instance if they're used to control the UI in another way.
 */
const ignoredPrintables = [
  ' ',
];

/**
 * ## Type-ahead mixin for form elements etc.
 *
 * Captures sequences of rapid key strokes,
 * and disables itself for a short time after capturing
 * to prevent accidental lookups.
 *
 * ### Usage
 *
 * First, attach the `onTypeAhead` handler to the `keypress` event like this:
 * ```vue
 * <template>
 *  <input @keypress="onTypeAhead($event.key)" type="button" value="test" />
 * </template>
 * ```
 *
 * Then use the `type-ahead` prop when using your component:
 *
 * ```vue
 * <template>
 *   <MyComponent :type-ahead="true" />
 * </template>
 * ```
 *
 * Type-ahead will then detect character sequences, which you can process
 * by implementing a `handleTypeAheadSequence` method:
 *
 * ```js
 * {
 *  handleTypeAheadSequence(sequence) {
 *    // here you can, for instance, select from a list of options
 *    // the one that starts with `sequence`
 *  }
 * }
 * ```
 */
export default {
  props: {
    /**
     * Enables type-ahead functionality as recommended by WAI-ARIA
     */
    typeAhead: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      typeAheadMemory: [],
      typeAheadTimer: null,
      typeAheadReleaseTimer: null,
    };
  },
  methods: {
    onTypeAhead(key) {
      if (!this.typeAhead) {
        return;
      }
      // we may also receive control keys here, so discard them
      if (key.length > 1 || ignoredPrintables.includes(key)) {
        return;
      }

      if (!this.typeAheadTimer && !this.typeAheadReleaseTimer) {
        this.typeAheadTimer = setTimeout(() => {
          this.typeAheadLookup();
          this.typeAheadTimer = null;
          this.typeAheadReleaseTimer = setTimeout(() => {
            this.typeAheadReleaseTimer = null;
          }, TYPEAHEAD_RELEASE_DELAY);
        }, TYPEAHEAD_DELAY);
      }
      if (!this.typeAheadReleaseTimer) {
        this.typeAheadMemory.push(key);
      }
    },
    typeAheadLookup() {
      const sequence = this.typeAheadMemory.join('');
      if (this.handleTypeAheadSequence) {
        this.handleTypeAheadSequence(sequence);
      }
    },
  },
};
